import { useState } from 'react';
import AppDialog from '../../../../components/common/AppDialog';
import { SVG } from '../../../../constants/assetLists';
import AppButton from '../../../../components/common/AppButton';
import { useOnBoardingContext } from '../../../../context/onBoarding/OnBoardingContextProvider';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import QUERY_KEYS from '../../../../constants/queryKeys';
import {
  getOnBoardingTargetSelector,
  handleGetOnBoardingDraftService,
} from '../../../../services/on-boarding';
import { isSuperAdmin } from '../../../../utils';
import DraftDetails from './DraftDetails';

function OnBoardingDialog() {
  const { setCurrentStep } = useOnBoardingContext();
  const [showModal, setShowModal] = useState(true);

  const navigate = useNavigate();

  const { data: draftData, isPending } = useQuery({
    queryKey: [QUERY_KEYS.GET_ON_BOARDING_DRAFT, getOnBoardingTargetSelector()],
    queryFn: handleGetOnBoardingDraftService,
    meta: { toastOnError: false },
  });

  const handleNextStep = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const user = draftData?.user ?? null;
  const isOnBoardingCompleted = !!(
    draftData?.completedOnboarding ?? user?.completedOnboarding
  );
  const shouldResyncOnboarding = !!(
    draftData?.shouldResyncOnboarding ?? user?.shouldResyncOnboarding
  );
  const isDraftExist = !!draftData;
  const isDraftValid = !!(isDraftExist && draftData?.isValidData);

  const handleNavigateToOperations = () => {
    if (user && !isOnBoardingCompleted) {
      setShowModal(false);
      return;
    }
    navigate(-1);
  };

  const renderHeader = () => {
    if (!isDraftExist) {
      return (
        <>
          <p className="m-0 mt-3 text-xl text-darkerGray font-bold text-center">
            בשלבים הבאים תגדירו את הארגון שלכם ותוכלו להתחיל לעבוד מיד.
          </p>
          <p className="m-0 text-xl text-darkerGray font-bold text-center">
            בכל שלב תוכלו לשמור ולהמשיך מאוחר יותר, או לדלג ולהגדיר מאוחר יותר.
          </p>
          <p className="m-0 mt-4 text-xl text-darkerGray font-bold text-center">
            כדי להפיק את המירב מהמערכת, מומלץ להשלים את כל השלבים כעת.
          </p>
        </>
      );
    } else if (!isDraftValid) {
      return (
        <>
          <p className="m-0 mt-3 text-xl text-darkerGray font-bold text-center">
            המערכת שלכם כמעט מוכנה, נשארו הגדרות מועטות ואפשר להתחיל לעבוד.
          </p>
        </>
      );
    } else if (
      (!isOnBoardingCompleted && isDraftValid) ||
      (shouldResyncOnboarding && isDraftValid)
    ) {
      return (
        <>
          <div className="mt-2 flex items-center gap-3">
            <div className="w-14 h-14">
              <img
                src={SVG.activateCheckIcon}
                alt="icon"
                className="w-full h-full object-contain"
              />
            </div>
            <p className="m-0 text-2xl text-textColor">ברכות, המערכת מוכנה</p>
          </div>

          <p className="m-0 mt-2 text-base text-darkGray text-center">
            אתם יכולים להתחיל, בלחיצה על "הפעלת המערכת" ישלחו לכל המשתמשים
            שהגדרתם לינקים להורדת האפליקציה.
          </p>

          <p className="m-0 text-base text-darkGray text-center">
            תוכלו להפעיל בכל שלב שתבחרו.
          </p>
        </>
      );
    } else if (isOnBoardingCompleted && isDraftValid) {
      return (
        <>
          <div className="mt-2 flex items-center gap-3">
            <div className="w-14 h-14">
              <img
                src={SVG.activeCheckIcon}
                alt="icon"
                className="w-full h-full object-contain"
              />
            </div>

            <p className="m-0 text-2xl text-textColor">מערכת פעילה</p>
          </div>

          <p className="m-0 mt-2 text-base text-darkGray text-center">
            בכל שלב ניתן לעדכן את הנתונים
          </p>
        </>
      );
    }
  };

  const renderContent = () => {
    if (!isPending && !isDraftExist && !isSuperAdmin()) {
      // A super admin only manages an organization the network owner already
      // activated — never starts a new onboarding of their own.
      return (
        <div className="flex-1 flex items-center justify-center">
          <p className="m-0 text-xl text-darkerGray font-bold text-center">
            הארגון עדיין לא הופעל על ידי בעל הרשת, לכן לא ניתן לערוך את עץ
            הארגון.
          </p>
        </div>
      );
    }
    if (!isDraftExist) {
      return (
        <div
          className="flex-1 flex items-center justify-center"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(7, 221, 202, 0.5) 0%, rgba(133, 255, 248, 0.5) 73.08%, rgba(39, 219, 215, 0.5) 100%)`,
          }}
        >
          <div className="flex-1 flex flex-col items-center gap-3 py-3">
            <div className="w-[190px] h-[185px]">
              <img
                src={SVG.cheqqItTickIcon}
                alt="tick"
                className="w-full h-full object-contain"
              />
            </div>

            <AppButton
              variant="custom"
              className="w-[198px] h-[64px] justify-center bg-linkBlue border-[4px] border-white text-white text-2xl font-bold hover:opacity-90"
              onClick={handleNextStep}
            >
              התחלה
            </AppButton>
          </div>
        </div>
      );
    } else {
      return (
        <DraftDetails
          draftData={draftData}
          isOnBoardingCompleted={isOnBoardingCompleted}
          isDraftValid={isDraftValid}
          shouldResyncOnboarding={shouldResyncOnboarding}
        />
      );
    }
  };

  return (
    <AppDialog
      showModel={showModal}
      title={'הגדרת האתר'}
      titleIcon={
        <img
          src={SVG.darkStarIcon}
          alt="icon"
          className="w-[20px] h-[20px] object-contain"
        />
      }
      onClose={handleNavigateToOperations}
      showHeaderLine={false}
      width={1366}
      fullHeight
      mainContentClasses="!px-0"
    >
      <div className="w-full h-full flex flex-col gap-4">
        <div className="px-3 flex-1 min-h-0 overflow-y-auto flex flex-col gap-4">
          <div className="mt-5 flex flex-col items-center">
            <p className="m-0 text-xl text-deepAqua font-bold text-center">
              ברוכים הבאים למערכת
            </p>
            <div className="mt-3 w-[140px] h-[24px]">
              <img
                src={SVG.logoDarkIcon}
                alt="logo"
                className="w-full h-full object-contain"
              />
            </div>

            {renderHeader()}
          </div>

          {renderContent()}
        </div>

        <div className="flex justify-end pt-4 px-3 border-t border-t-gray70">
          <AppButton onClick={handleNavigateToOperations}>סגירה</AppButton>
        </div>
      </div>
    </AppDialog>
  );
}

export default OnBoardingDialog;
